import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { Loading } from '@components/common/Loading';
import { WorkflowSteps } from '@components/workflow/WorkflowSteps';
import { useGetProjectQuery } from '@services/projectsApi';
import { useToast } from '@hooks/useToast';
import { videoAssets, audioAssets, imageAssets } from '../../mocks/data/editorAssets';
import { getProjectScenes } from '@services/sceneApi';
import { synthesizeSpeech } from '@services/ttsApi';
export default function VideoEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { success: showSuccess, error: showError } = useToast();
    const { data: project, isLoading } = useGetProjectQuery(id);
    console.log('=== 视频编辑器页面加载 ===', new Date().toISOString());
    const [clips, setClips] = useState([]);
    const [scenes, setScenes] = useState([]);
    const [loadingScenes, setLoadingScenes] = useState(false);
    const [dialogueAudios, setDialogueAudios] = useState([]);
    const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
    const [voiceProgress, setVoiceProgress] = useState({ current: 0, total: 0 });
    const [timelineScale, setTimelineScale] = useState(1); // 时间线缩放比例
    const shouldStopGeneratingRef = useRef(false); // 停止生成标志 - 使用 ref 确保立即生效
    const previousScenesRef = useRef([]); // 保存上一次的场景数据,用于检测变化
    // 调试: 监控 dialogueAudios 变化
    useEffect(() => {
        console.log('dialogueAudios 更新:', dialogueAudios.length, '个对话');
        dialogueAudios.forEach(d => {
            console.log('  -', d.character, ':', d.text.substring(0, 20), 'URL:', d.audioUrl);
        });
    }, [dialogueAudios]);
    const [selectedClip, setSelectedClip] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [activeTab, setActiveTab] = useState('video');
    const [draggedAsset, setDraggedAsset] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(videoAssets[0].url);
    const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
    const timelineRef = useRef(null);
    const playIntervalRef = useRef(null);
    const dialogueAudioRefs = useRef(new Map());
    const playingDialoguesRef = useRef(new Set()); // 记录正在播放的对话
    // 加载场景数据
    useEffect(() => {
        if (id) {
            loadScenes();
        }
    }, [id]);
    // 检测场景图片变化并自动重新加载
    useEffect(() => {
        if (scenes.length > 0 && previousScenesRef.current.length > 0) {
            // 检查场景图片是否有变化
            const hasImageChanged = scenes.some((scene, index) => {
                const prevScene = previousScenesRef.current[index];
                return prevScene && scene.aiGeneratedImage !== prevScene.aiGeneratedImage;
            });
            if (hasImageChanged) {
                console.log('检测到场景图片变化,重新加载时间线');
                loadScenes();
            }
        }
        // 更新上一次的场景数据
        if (scenes.length > 0) {
            previousScenesRef.current = scenes;
        }
    }, [scenes]);
    // 监听路由变化,重新加载场景
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && id) {
                console.log('页面可见,重新加载场景');
                loadScenes();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [id]);
    const loadScenes = async () => {
        if (!id)
            return;
        try {
            setLoadingScenes(true);
            const data = await getProjectScenes(id);
            setScenes(data);
            // 将场景转换为时间线片段
            if (data.length > 0) {
                let currentTime = 0;
                const sceneClips = [];
                // 预加载图片并验证
                for (const scene of data) {
                    let imageUrl = scene.aiGeneratedImage;
                    // 如果有图片URL,尝试预加载验证
                    if (imageUrl) {
                        try {
                            await new Promise((resolve, reject) => {
                                const img = new Image();
                                img.onload = () => {
                                    console.log('✓ 场景图片加载成功:', scene.sceneNumber);
                                    resolve();
                                };
                                img.onerror = () => {
                                    console.warn('✗ 场景图片加载失败:', scene.sceneNumber, '使用占位图');
                                    reject();
                                };
                                img.src = imageUrl;
                                // 设置超时
                                setTimeout(() => reject(), 3000);
                            });
                        }
                        catch (error) {
                            // 图片加载失败,使用占位图
                            imageUrl = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="180"%3E%3Crect width="320" height="180" fill="%231a1f2e"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23666" font-size="14"%3E场景 ${scene.sceneNumber}%3C/text%3E%3C/svg%3E`;
                        }
                    }
                    else {
                        // 没有图片,使用占位图
                        imageUrl = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="180"%3E%3Crect width="320" height="180" fill="%231a1f2e"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23666" font-size="14"%3E场景 ${scene.sceneNumber}%3C/text%3E%3C/svg%3E`;
                    }
                    const clip = {
                        id: `scene-${scene.id}`,
                        type: 'image',
                        name: `场景${scene.sceneNumber}: ${scene.title}`,
                        thumbnail: imageUrl,
                        url: imageUrl,
                        startTime: currentTime,
                        duration: scene.duration || 3, // 默认3秒
                        track: 0,
                        sceneId: scene.id
                    };
                    currentTime += clip.duration;
                    sceneClips.push(clip);
                }
                setClips(sceneClips);
                // 设置第一个场景为预览
                if (sceneClips[0]) {
                    setPreviewUrl(sceneClips[0].url);
                }
            }
        }
        catch (error) {
            console.error('加载场景失败:', error);
            showError('加载场景失败，请返回分镜设计页面');
        }
        finally {
            setLoadingScenes(false);
        }
    };
    // 获取当前tab的素材
    const currentAssets = activeTab === 'video' ? videoAssets : activeTab === 'audio' ? audioAssets : imageAssets;
    // 计算总时长
    const totalDuration = Math.max(...clips.map(c => c.startTime + c.duration), 10);
    // 处理时间轴点击/拖动
    const updateTimeFromPosition = (clientX) => {
        if (!timelineRef.current)
            return;
        const rect = timelineRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(x / rect.width, 1));
        const newTime = percentage * totalDuration;
        setCurrentTime(newTime);
    };
    const handleTimelineMouseDown = (e) => {
        e.preventDefault();
        setIsDraggingTimeline(true);
        updateTimeFromPosition(e.clientX);
    };
    // 播放控制
    useEffect(() => {
        if (isPlaying) {
            // 开始播放
            playIntervalRef.current = setInterval(() => {
                setCurrentTime(prev => {
                    const newTime = prev + 0.1;
                    if (newTime >= totalDuration) {
                        // 播放结束
                        setIsPlaying(false);
                        return 0;
                    }
                    return newTime;
                });
            }, 100); // 每100ms更新一次
        }
        else {
            // 停止播放
            if (playIntervalRef.current) {
                clearInterval(playIntervalRef.current);
                playIntervalRef.current = null;
            }
        }
        return () => {
            if (playIntervalRef.current) {
                clearInterval(playIntervalRef.current);
            }
        };
    }, [isPlaying, totalDuration]);
    // 播放对话语音 - 改进版:避免重复播放,使用实际音频时长
    useEffect(() => {
        if (!isPlaying) {
            // 暂停所有对话音频
            dialogueAudioRefs.current.forEach(audio => {
                if (!audio.paused) {
                    audio.pause();
                    audio.currentTime = 0;
                }
            });
            playingDialoguesRef.current.clear();
            return;
        }
        // 检查是否有对话需要在当前时间播放
        dialogueAudios.forEach(dialogue => {
            const audioKey = `${dialogue.sceneId}-${dialogue.character}-${dialogue.startTime}`;
            const audio = dialogueAudioRefs.current.get(audioKey);
            if (!audio) {
                return;
            }
            // 使用音频的实际时长(如果已加载)或估算时长
            const actualDuration = audio.duration && !isNaN(audio.duration) ? audio.duration : dialogue.duration;
            const dialogueEndTime = dialogue.startTime + actualDuration;
            // 如果当前时间在对话时间范围内
            if (currentTime >= dialogue.startTime && currentTime < dialogueEndTime) {
                // 如果音频还没开始播放
                if (audio.paused && !playingDialoguesRef.current.has(audioKey)) {
                    const offset = currentTime - dialogue.startTime;
                    audio.currentTime = offset;
                    playingDialoguesRef.current.add(audioKey);
                    audio.play().then(() => {
                        console.log('✓ 开始播放对话:', dialogue.character, '实际时长:', actualDuration.toFixed(2), 's');
                    }).catch(err => {
                        console.error('对话音频播放失败:', err);
                        playingDialoguesRef.current.delete(audioKey);
                    });
                    // 监听播放结束事件
                    audio.onended = () => {
                        console.log('✓ 对话播放完成:', dialogue.character);
                        playingDialoguesRef.current.delete(audioKey);
                    };
                }
            }
            else {
                // 如果不在时间范围内,且正在播放,则暂停
                if (!audio.paused && playingDialoguesRef.current.has(audioKey)) {
                    audio.pause();
                    audio.currentTime = 0;
                    playingDialoguesRef.current.delete(audioKey);
                }
            }
        });
    }, [currentTime, isPlaying, dialogueAudios]);
    // 根据当前时间更新预览
    useEffect(() => {
        // 如果没有片段,不做任何处理
        if (clips.length === 0)
            return;
        // 找到当前时间对应的片段
        const currentClip = clips.find(clip => clip.track === 0 && // 只查找视频/图片轨道
            currentTime >= clip.startTime &&
            currentTime < clip.startTime + clip.duration);
        if (currentClip && currentClip.type !== 'audio') {
            setPreviewUrl(currentClip.url);
            setSelectedClip(currentClip);
        }
        else if (clips.length > 0 && currentTime === 0) {
            // 如果当前时间是0且没找到片段,显示第一个场景
            const firstClip = clips.find(clip => clip.track === 0 && clip.type !== 'audio');
            if (firstClip) {
                setPreviewUrl(firstClip.url);
                setSelectedClip(firstClip);
            }
        }
    }, [currentTime, clips]);
    // 全局鼠标事件监听
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDraggingTimeline) {
                updateTimeFromPosition(e.clientX);
            }
        };
        const handleMouseUp = () => {
            setIsDraggingTimeline(false);
        };
        if (isDraggingTimeline) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingTimeline, totalDuration]);
    // 添加素材到时间线
    const handleAddClip = (asset, track) => {
        // 如果没有指定轨道，根据类型自动选择
        let targetTrack = track;
        if (targetTrack === undefined) {
            if (asset.type === 'audio') {
                // 音频默认放到背景音乐轨道
                targetTrack = 2;
            }
            else if (asset.type === 'video' || asset.type === 'image') {
                targetTrack = 0;
            }
            else {
                targetTrack = 0;
            }
        }
        const newClip = {
            id: `clip-${Date.now()}`,
            type: asset.type,
            name: asset.name,
            thumbnail: asset.thumbnail,
            url: asset.url,
            startTime: currentTime,
            duration: asset.duration || 3,
            track: targetTrack
        };
        setClips([...clips, newClip]);
        showSuccess(`已添加 ${asset.name}`);
    };
    // 删除片段
    const handleDeleteClip = (clipId) => {
        setClips(clips.filter(c => c.id !== clipId));
        if (selectedClip?.id === clipId) {
            setSelectedClip(null);
        }
        showSuccess('已删除片段');
    };
    // 拖拽开始
    const handleDragStart = (asset) => {
        setDraggedAsset(asset);
    };
    // 拖拽结束
    const handleDrop = (track) => {
        if (draggedAsset) {
            handleAddClip(draggedAsset, track);
            setDraggedAsset(null);
        }
    };
    // 播放/暂停控制
    const handlePlayPause = () => {
        console.log('点击播放/暂停, 当前状态:', isPlaying, '对话数量:', dialogueAudios.length);
        if (isPlaying) {
            setIsPlaying(false);
            // 暂停所有对话音频
            dialogueAudioRefs.current.forEach(audio => audio.pause());
        }
        else {
            setIsPlaying(true);
        }
    };
    // 生成所有场景的对话语音
    const handleGenerateAllVoices = async () => {
        if (scenes.length === 0) {
            showError('没有场景数据');
            return;
        }
        try {
            setIsGeneratingVoice(true);
            shouldStopGeneratingRef.current = false;
            // 先统计总对话数量
            let totalDialogues = 0;
            const sceneDialogues = [];
            for (const scene of scenes) {
                const dialogueText = scene.dialogue || scene.description || scene.action || '';
                const dialogues = parseDialogue(dialogueText);
                if (dialogues.length > 0) {
                    sceneDialogues.push({ scene, dialogues });
                    totalDialogues += dialogues.length;
                }
            }
            if (totalDialogues === 0) {
                showError('没有找到可生成的对话内容');
                setIsGeneratingVoice(false);
                return;
            }
            // 设置总数
            setVoiceProgress({ current: 0, total: totalDialogues });
            const allDialogues = [];
            const sceneDialogueDurations = new Map();
            let processedCount = 0;
            // 生成语音
            for (const { scene, dialogues } of sceneDialogues) {
                if (shouldStopGeneratingRef.current) {
                    showSuccess(`已停止生成，保留了 ${allDialogues.length} 段对话语音`);
                    break;
                }
                const sceneClip = clips.find(c => c.sceneId === scene.id && c.type === 'image');
                const sceneStartTime = sceneClip?.startTime ?? 0; // 使用 ?? 而不是 ||
                let dialogueStartTime = sceneStartTime;
                let sceneTotalDialogueDuration = 0;
                console.log('场景:', scene.sceneNumber, 'sceneClip:', sceneClip?.name, 'startTime:', sceneStartTime);
                for (const dialogue of dialogues) {
                    if (shouldStopGeneratingRef.current) {
                        break;
                    }
                    try {
                        const voice = selectVoiceForCharacter(dialogue.character);
                        const response = await synthesizeSpeech({
                            text: dialogue.text,
                            voice: voice,
                            volume: 80
                        });
                        if (response.success) {
                            const estimatedDuration = Number(response.data.duration) || 3; // 估算时长
                            const newDialogue = {
                                sceneId: scene.id,
                                character: dialogue.character,
                                text: dialogue.text,
                                audioUrl: response.data.audioUrl,
                                startTime: dialogueStartTime,
                                duration: estimatedDuration
                            };
                            allDialogues.push(newDialogue);
                            // 立即更新 dialogueAudios 状态
                            setDialogueAudios(prev => [...prev, newDialogue]);
                            // 创建音频元素
                            const audio = new Audio(response.data.audioUrl);
                            audio.preload = 'auto';
                            const audioKey = `${newDialogue.sceneId}-${newDialogue.character}-${newDialogue.startTime}`;
                            dialogueAudioRefs.current.set(audioKey, audio);
                            // 监听音频加载完成,获取实际时长
                            audio.addEventListener('loadedmetadata', () => {
                                const actualDuration = audio.duration;
                                console.log('✓ 音频加载完成:', dialogue.character, '估算时长:', estimatedDuration.toFixed(2), 's', '实际时长:', actualDuration.toFixed(2), 's');
                                // 更新 dialogueAudios 中的时长
                                setDialogueAudios(prev => prev.map(d => d.sceneId === newDialogue.sceneId &&
                                    d.character === newDialogue.character &&
                                    d.startTime === newDialogue.startTime
                                    ? { ...d, duration: actualDuration }
                                    : d));
                                // 更新时间线片段的时长
                                setClips(prev => prev.map(clip => clip.id === `dialogue-${newDialogue.sceneId}-${newDialogue.character}-${dialogueStartTime}`
                                    ? { ...clip, duration: actualDuration }
                                    : clip));
                            });
                            // 立即添加到时间线(使用估算时长)
                            const dialogueClip = {
                                id: `dialogue-${newDialogue.sceneId}-${newDialogue.character}-${dialogueStartTime}`,
                                type: 'audio',
                                name: `${newDialogue.character}: ${newDialogue.text.substring(0, 20)}...`,
                                thumbnail: '🎤',
                                url: newDialogue.audioUrl,
                                startTime: dialogueStartTime,
                                duration: estimatedDuration,
                                track: 1,
                                sceneId: newDialogue.sceneId
                            };
                            setClips(prev => [...prev, dialogueClip]);
                            dialogueStartTime += estimatedDuration + 0.3;
                            sceneTotalDialogueDuration += estimatedDuration + 0.3;
                        }
                        processedCount++;
                        setVoiceProgress({ current: processedCount, total: totalDialogues });
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                    catch (error) {
                        console.error(`生成对话语音失败 (${dialogue.character}):`, error);
                        processedCount++;
                        setVoiceProgress({ current: processedCount, total: totalDialogues });
                    }
                }
                // 记录场景的对话总时长并立即调整场景时长
                if (sceneTotalDialogueDuration > 0) {
                    sceneDialogueDurations.set(scene.id, sceneTotalDialogueDuration);
                    // 立即调整该场景的时长
                    setClips(prev => {
                        const imageClips = prev.filter(c => c.type === 'image');
                        const otherClips = prev.filter(c => c.type !== 'image');
                        // 更新场景时长
                        const updatedImageClips = imageClips.map(clip => {
                            if (clip.sceneId === scene.id) {
                                const newDuration = Math.max(clip.duration, Math.ceil(sceneTotalDialogueDuration) + 1);
                                return { ...clip, duration: newDuration };
                            }
                            return clip;
                        });
                        // 重新计算所有场景的开始时间
                        let currentTime = 0;
                        const reTimedImageClips = updatedImageClips.map(clip => {
                            const newClip = { ...clip, startTime: currentTime };
                            currentTime += newClip.duration;
                            return newClip;
                        });
                        // 更新对话片段的开始时间（相对于场景）
                        const updatedOtherClips = otherClips.map(clip => {
                            if (clip.sceneId) {
                                const sceneClip = reTimedImageClips.find(c => c.sceneId === clip.sceneId);
                                if (sceneClip) {
                                    // 保持对话在场景内的相对位置
                                    const relativeTime = clip.startTime - (imageClips.find(c => c.sceneId === clip.sceneId)?.startTime || 0);
                                    return { ...clip, startTime: sceneClip.startTime + relativeTime };
                                }
                            }
                            return clip;
                        });
                        return [...reTimedImageClips, ...updatedOtherClips];
                    });
                }
            }
            if (!shouldStopGeneratingRef.current) {
                showSuccess(`成功生成 ${allDialogues.length} 段对话语音！已自动调整场景时长。`);
            }
        }
        catch (error) {
            console.error('生成语音失败:', error);
            showError('生成语音失败: ' + error.message);
        }
        finally {
            setIsGeneratingVoice(false);
            shouldStopGeneratingRef.current = false;
            setVoiceProgress({ current: 0, total: 0 });
        }
    };
    // 解析对话内容
    const parseDialogue = (dialogueText) => {
        if (!dialogueText)
            return [];
        const dialogues = [];
        // 匹配格式：**角色名**：对话内容
        const regex = /\*\*([^*]+)\*\*[：:]\s*([^*\n]+)/g;
        let match;
        while ((match = regex.exec(dialogueText)) !== null) {
            const character = match[1].trim();
            let text = match[2].trim();
            // 移除括号内的动作描述（包括中文括号和英文括号）
            text = text.replace(/[（(][^）)]*[）)]/g, '').trim();
            if (character && text) {
                dialogues.push({ character, text });
            }
        }
        return dialogues;
    };
    // 根据角色选择合适的音色
    const selectVoiceForCharacter = (characterName) => {
        // 简单的性别判断逻辑
        const femaleKeywords = ['女', '妹', '母', '姐', '小雨', '雨'];
        const maleKeywords = ['男', '父', '兄', '哥', '艾登'];
        const isFemale = femaleKeywords.some(keyword => characterName.includes(keyword));
        const isMale = maleKeywords.some(keyword => characterName.includes(keyword));
        if (isFemale) {
            // 女声音色
            return 'xiaoyun'; // 温柔女声
        }
        else if (isMale) {
            // 男声音色
            return 'zhichu'; // 沉稳男声
        }
        else {
            // 默认中性音色
            return 'sicheng';
        }
    };
    const handleExport = () => {
        showSuccess('正在导出视频...');
        // 这里实现导出逻辑
    };
    const handleNext = () => {
        if (!id)
            return;
        navigate(`/project/${id}/publish`);
    };
    if (isLoading || loadingScenes) {
        return _jsx(Loading, { fullScreen: true, text: "\u52A0\u8F7D\u4E2D..." });
    }
    if (!project) {
        return (_jsxs("div", { className: "flex min-h-[60vh] flex-col items-center justify-center", children: [_jsx("h2", { className: "mb-4 text-2xl font-semibold text-gray-800", children: "\u9879\u76EE\u4E0D\u5B58\u5728" }), _jsx(Button, { onClick: () => navigate('/topics'), children: "\u8FD4\u56DE\u9009\u9898" })] }));
    }
    return (_jsxs("div", { className: "min-h-screen bg-dark-700", children: [_jsx("div", { className: "border-b border-gray-800 bg-dark-600", children: _jsx(WorkflowSteps, { projectId: id, currentStep: "video" }) }), _jsx("div", { className: "border-b border-gray-800 bg-dark-600 px-6 py-4", children: _jsxs("div", { className: "mx-auto flex max-w-[1440px] items-center justify-between", children: [_jsxs("div", { children: [_jsxs("h1", { className: "text-2xl font-bold text-gray-100", children: [project.title, " - \u89C6\u9891\u7F16\u8F91"] }), _jsx("p", { className: "mt-1 text-sm text-gray-400", children: scenes.length > 0 ? `共 ${scenes.length} 个场景` : '暂无场景' })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("button", { onClick: () => {
                                        if (isGeneratingVoice) {
                                            // 停止生成
                                            shouldStopGeneratingRef.current = true;
                                        }
                                        else {
                                            // 开始生成
                                            handleGenerateAllVoices();
                                        }
                                    }, disabled: scenes.length === 0, className: "rounded bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed", children: isGeneratingVoice ? '⏹ 停止生成' : '🎤 生成对话语音' }), isGeneratingVoice && (_jsxs("div", { className: "flex items-center gap-2 text-sm text-gray-300", children: [_jsx("div", { className: "h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" }), _jsxs("span", { children: ["(", voiceProgress.current, "/", voiceProgress.total, ")"] })] })), dialogueAudios.length > 0 && (_jsxs("div", { className: "text-sm text-green-400", children: ["\u2705 \u5DF2\u751F\u6210 ", dialogueAudios.length, " \u6BB5\u8BED\u97F3"] })), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => navigate(`/project/${id}/storyboard`), children: "\u2190 \u8FD4\u56DE\u5206\u955C" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: handleExport, children: "\u5BFC\u51FA\u89C6\u9891" }), _jsx(Button, { variant: "primary", size: "sm", onClick: handleNext, children: "\u4E0B\u4E00\u6B65\uFF1A\u53D1\u5E03 \u2192" })] })] }) }), _jsx("div", { className: "mx-auto max-w-[1440px] p-6", children: _jsxs("div", { className: "flex flex-row flex-nowrap gap-6", children: [_jsxs("div", { className: "w-64 flex-shrink-0 rounded-lg border border-gray-800 bg-dark-600 p-4 overflow-y-auto", style: { maxHeight: 'calc(100vh - 250px)' }, children: [_jsx("h2", { className: "mb-4 text-sm font-semibold text-gray-100", children: "\u7D20\u6750\u5E93" }), _jsxs("div", { className: "mb-4 flex gap-2", children: [_jsx("button", { onClick: () => setActiveTab('video'), className: `rounded px-3 py-1 text-xs ${activeTab === 'video'
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-dark-500 text-gray-300 hover:bg-dark-400'}`, children: "\u89C6\u9891" }), _jsx("button", { onClick: () => setActiveTab('audio'), className: `rounded px-3 py-1 text-xs ${activeTab === 'audio'
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-dark-500 text-gray-300 hover:bg-dark-400'}`, children: "\u97F3\u9891" }), _jsx("button", { onClick: () => setActiveTab('image'), className: `rounded px-3 py-1 text-xs ${activeTab === 'image'
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-dark-500 text-gray-300 hover:bg-dark-400'}`, children: "\u56FE\u7247" })] }), _jsx("div", { className: "space-y-2", children: currentAssets.map((asset) => (_jsxs("div", { draggable: true, onDragStart: () => handleDragStart(asset), onClick: () => {
                                            if (asset.type !== 'audio') {
                                                setPreviewUrl(asset.url);
                                            }
                                        }, onDoubleClick: () => handleAddClip(asset), className: "cursor-move rounded bg-dark-500 p-2 hover:bg-dark-400 transition-colors", title: "\u62D6\u62FD\u5230\u65F6\u95F4\u7EBF\u6216\u53CC\u51FB\u6DFB\u52A0", children: [asset.type === 'audio' ? (_jsx("div", { className: "mb-1 aspect-video rounded bg-dark-400 flex items-center justify-center text-4xl", children: asset.thumbnail })) : (_jsx("img", { src: asset.thumbnail, alt: asset.name, className: "mb-1 aspect-video w-full rounded object-cover" })), _jsx("p", { className: "text-xs text-gray-300 truncate", children: asset.name }), _jsxs("div", { className: "flex justify-between text-xs text-gray-500 mt-1", children: [_jsx("span", { children: asset.duration ? `${asset.duration}s` : '' }), _jsx("span", { children: asset.size })] })] }, asset.id))) })] }), _jsxs("div", { className: "flex flex-1 flex-col gap-6", style: { minWidth: 0 }, children: [_jsx("div", { className: "rounded-lg border border-gray-800 bg-dark-600 p-4", children: _jsx("div", { className: "aspect-video w-full rounded bg-black overflow-hidden flex items-center justify-center", children: previewUrl ? (_jsx("img", { src: previewUrl, alt: "\u9884\u89C8", className: "w-full h-full object-contain", onError: (e) => {
                                                console.error('图片加载失败:', previewUrl);
                                                e.currentTarget.style.display = 'none';
                                            } })) : (_jsx("div", { className: "text-gray-500 text-sm", children: "\u6682\u65E0\u9884\u89C8" })) }) }), _jsx("div", { className: "rounded-lg border border-gray-800 bg-dark-600 px-6 py-3", children: _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("button", { onClick: handlePlayPause, className: "rounded bg-primary-600 p-2 text-white hover:bg-primary-700 w-10 h-10 flex items-center justify-center", title: isPlaying ? '暂停' : '播放', children: isPlaying ? '⏸' : '▶' }), _jsx("div", { className: "flex-1", children: _jsx("input", { type: "range", min: "0", max: totalDuration, step: "0.1", value: currentTime, onChange: (e) => setCurrentTime(parseFloat(e.target.value)), className: "w-full" }) }), _jsxs("span", { className: "text-sm text-gray-400 w-24 text-right", children: [Math.floor(currentTime), ":", Math.floor((currentTime % 1) * 60).toString().padStart(2, '0'), " / ", Math.floor(totalDuration), ":", Math.floor((totalDuration % 1) * 60).toString().padStart(2, '0')] })] }) }), _jsxs("div", { className: "rounded-lg border border-gray-800 bg-dark-600 p-4", style: { width: '100%', overflow: 'hidden' }, children: [_jsxs("div", { className: "flex items-center justify-between mb-2", children: [_jsx("h3", { className: "text-sm font-semibold text-gray-100", children: "\u65F6\u95F4\u7EBF" }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { onClick: () => setTimelineScale(prev => Math.max(0.5, prev - 0.25)), className: "rounded bg-dark-500 px-2 py-1 text-xs text-gray-300 hover:bg-dark-400", title: "\u7F29\u5C0F", children: "\u2212" }), _jsxs("span", { className: "text-xs text-gray-400", children: [Math.round(timelineScale * 100), "%"] }), _jsx("button", { onClick: () => setTimelineScale(prev => Math.min(3, prev + 0.25)), className: "rounded bg-dark-500 px-2 py-1 text-xs text-gray-300 hover:bg-dark-400", title: "\u653E\u5927", children: "+" })] }), _jsx("div", { className: "text-xs text-gray-400", children: "\u63D0\u793A: \u62D6\u62FD\u7D20\u6750\u5230\u8F68\u9053 | \u53CC\u51FB\u7D20\u6750\u6DFB\u52A0 | \u70B9\u51FB\u7247\u6BB5\u9009\u4E2D | \u53F3\u952E\u5220\u9664" })] })] }), _jsx("div", { className: "overflow-x-auto overflow-y-hidden w-full", style: { maxHeight: '300px' }, children: _jsxs("div", { className: "space-y-2 relative", ref: timelineRef, style: { width: `${Math.max(800, totalDuration * 40 * timelineScale)}px`, minWidth: '100%' }, children: [[
                                                        { index: 0, label: '视频' },
                                                        { index: 1, label: '音频（对白）' },
                                                        { index: 2, label: '音频（背景）' },
                                                        { index: 3, label: '音频（音效）' }
                                                    ].map(({ index: trackIndex, label }) => (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: "w-16 text-xs text-gray-400 flex-shrink-0", children: label }), _jsxs("div", { className: "relative h-12 flex-1 rounded bg-dark-500 cursor-pointer select-none", onDragOver: (e) => e.preventDefault(), onDrop: () => handleDrop(trackIndex), onMouseDown: handleTimelineMouseDown, children: [_jsx("div", { className: "absolute inset-0 flex", children: Array.from({ length: Math.ceil(totalDuration) }).map((_, i) => (_jsx("div", { className: "flex-shrink-0 border-l border-gray-600", style: { width: `${40 * timelineScale}px` }, children: _jsxs("span", { className: "text-xs text-gray-500 ml-1", children: [i, "s"] }) }, i))) }), clips
                                                                        .filter((clip) => clip.track === trackIndex)
                                                                        .map((clip) => (_jsxs("div", { onClick: () => {
                                                                            setSelectedClip(clip);
                                                                            if (clip.type !== 'audio') {
                                                                                setPreviewUrl(clip.url);
                                                                            }
                                                                        }, onContextMenu: (e) => {
                                                                            e.preventDefault();
                                                                            if (window.confirm(`确定删除 ${clip.name}?`)) {
                                                                                handleDeleteClip(clip.id);
                                                                            }
                                                                        }, className: `absolute h-full cursor-pointer rounded px-2 py-1 text-xs flex items-center gap-1 overflow-hidden ${selectedClip?.id === clip.id
                                                                            ? 'bg-primary-600 text-white ring-2 ring-primary-400'
                                                                            : clip.type === 'audio'
                                                                                ? 'bg-green-600 text-white hover:bg-green-700'
                                                                                : clip.type === 'image'
                                                                                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                                                    : 'bg-blue-500 text-white hover:bg-blue-600'}`, style: {
                                                                            left: `${(clip.startTime / totalDuration) * 100}%`,
                                                                            width: `${(clip.duration / totalDuration) * 100}%`,
                                                                        }, title: `${clip.name} (${clip.duration}s)`, children: [clip.type !== 'audio' && clip.thumbnail && (_jsx("img", { src: clip.thumbnail, alt: "", className: "h-8 w-8 rounded object-cover flex-shrink-0", onError: (e) => {
                                                                                    // 图片加载失败时隐藏
                                                                                    e.currentTarget.style.display = 'none';
                                                                                } })), _jsx("span", { className: "truncate flex-1", children: clip.name })] }, clip.id)))] })] }, trackIndex))), _jsx("div", { className: "absolute top-0 w-0.5 bg-red-500 pointer-events-none z-10", style: {
                                                            left: `calc(4rem + 0.5rem + ${(currentTime / totalDuration) * 100}%)`,
                                                            height: 'calc(100% - 0.5rem)',
                                                            marginLeft: '-1px'
                                                        }, children: _jsx("div", { className: "absolute -top-2 -left-2 w-4 h-4 bg-red-500 rounded-full pointer-events-auto cursor-grab active:cursor-grabbing", title: "\u62D6\u52A8\u8C03\u6574\u65F6\u95F4" }) })] }) })] }), false && (_jsxs("div", { className: "w-64 flex-shrink-0 rounded-lg border border-gray-800 bg-dark-600 p-4 overflow-y-auto", style: { maxHeight: 'calc(100vh - 250px)' }, children: [_jsx("h2", { className: "mb-4 text-sm font-semibold text-gray-100", children: "\u7279\u6548" }), _jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "rounded bg-dark-500 p-3", children: [_jsx("p", { className: "mb-2 text-xs font-medium text-gray-100", children: "\u8F6C\u573A" }), _jsx("div", { className: "grid grid-cols-2 gap-2", children: ['淡入淡出', '擦除', '推移', '溶解'].map((effect) => (_jsx("button", { className: "rounded bg-dark-400 px-2 py-1 text-xs text-gray-300 hover:bg-dark-300", children: effect }, effect))) })] }), _jsxs("div", { className: "rounded bg-dark-500 p-3", children: [_jsx("p", { className: "mb-2 text-xs font-medium text-gray-100", children: "\u6EE4\u955C" }), _jsx("div", { className: "grid grid-cols-2 gap-2", children: ['黑白', '复古', '鲜艳', '柔和'].map((filter) => (_jsx("button", { className: "rounded bg-dark-400 px-2 py-1 text-xs text-gray-300 hover:bg-dark-300", children: filter }, filter))) })] }), selectedClip && (_jsxs("div", { className: "rounded bg-dark-500 p-3", children: [_jsx("p", { className: "mb-2 text-xs font-medium text-gray-100", children: "\u7247\u6BB5\u5C5E\u6027" }), _jsxs("div", { className: "space-y-2", children: [_jsxs("div", { children: [_jsx("label", { className: "mb-1 block text-xs text-gray-400", children: "\u97F3\u91CF" }), _jsx("input", { type: "range", min: "0", max: "100", className: "w-full" })] }), _jsxs("div", { children: [_jsx("label", { className: "mb-1 block text-xs text-gray-400", children: "\u901F\u5EA6" }), _jsx("input", { type: "range", min: "50", max: "200", defaultValue: "100", className: "w-full" })] })] })] }))] })] }))] })] }) })] }));
}
